import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShoppingBag, Clock, User, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CustomerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: orders, isLoading: ordersLoading } = useQuery({
    queryKey: ['customer-orders', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          store_profiles:florist_id (
            store_name
          )
        `)
        .eq('customer_id', user.id);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  const activeOrders = orders?.filter(order => 
    ['pending', 'processing', 'ready'].includes(order.status)
  ).length || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[brand-accent] via-[#FAF7F0] to-[brand-accent]">
      <main className="container mx-auto px-4 py-8 pt-24 max-w-6xl">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-brand-secondary mb-2">Welcome back, {user?.user_metadata?.full_name || 'Customer'}!</h1>
          <p className="text-gray-600">Here's an overview of your flower orders and activity</p>
        </div>

        <div className="grid gap-6">
          {/* Dashboard stats - Orders only */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Card className="relative overflow-hidden rounded-xl bg-white px-6 pb-12 pt-6 shadow-lg border border-brand-primary/10 hover:shadow-xl transition-shadow">
                <dt>
                  <div className="absolute rounded-xl bg-gradient-to-br from-[brand-primary] to-[#e63e1c] p-3 shadow-md">
                    <ShoppingBag className="h-6 w-6 text-white" aria-hidden="true" />
                  </div>
                  <p className="ml-16 text-sm font-medium text-gray-600">
                    Total Orders
                  </p>
                </dt>
                <dd className="ml-16 flex items-baseline pb-6 sm:pb-7 pt-2">
                  <p className="text-3xl font-bold bg-gradient-to-r from-[brand-primary] to-[#e63e1c] bg-clip-text text-transparent">{orders?.length || 0}</p>
                </dd>
              </Card>
              
              <Card className="relative overflow-hidden rounded-xl bg-white px-6 pb-12 pt-6 shadow-lg border border-brand-primary/10 hover:shadow-xl transition-shadow">
                <dt>
                  <div className="absolute rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 p-3 shadow-md">
                    <Clock className="h-6 w-6 text-white" aria-hidden="true" />
                  </div>
                  <p className="ml-16 text-sm font-medium text-gray-600">
                    Active Orders
                  </p>
                </dt>
                <dd className="ml-16 flex items-baseline pb-6 sm:pb-7 pt-2">
                  <p className="text-3xl font-bold bg-gradient-to-r from-emerald-500 to-emerald-600 bg-clip-text text-transparent">{activeOrders}</p>
                </dd>
              </Card>
            </div>
          </div>
          
          {/* Profile Settings Section */}
          <Card className="rounded-xl bg-white shadow-lg border border-brand-primary/10 overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-[brand-accent] to-white border-b border-brand-primary/10">
              <CardTitle className="text-brand-secondary">Profile Settings</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="rounded-lg bg-[#967F81]/10 p-2">
                    <Settings className="h-5 w-5 text-brand-primary" />
                  </div>
                  <span className="text-gray-700">Manage your profile and preferences</span>
                </div>
                <Button 
                  onClick={() => navigate('/profile-settings')}
                  className="bg-[#967F81] hover:bg-[#e63e1c] text-white shadow-md"
                >
                  Edit Profile
                </Button>
              </div>
            </CardContent>
          </Card>
          
          {/* Recent Orders Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-brand-secondary">Your Orders</h2>
              {orders && orders.length > 0 && (
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/search')}
                  className="border-brand-primary text-brand-primary bg-[#967F81] hover:bg-[#843329]/10"
                >
                  Order More Flowers
                </Button>
              )}
            </div>
            {ordersLoading ? (
              <Card className="rounded-xl shadow-lg border border-brand-primary/10">
                <CardContent className="flex justify-center p-12">
                  <div className="text-center">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-primary border-r-transparent mb-4"></div>
                    <p className="text-gray-600">Loading your orders...</p>
                  </div>
                </CardContent>
              </Card>
            ) : orders?.length === 0 ? (
              <Card className="rounded-xl bg-white shadow-lg border border-brand-primary/10 overflow-hidden">
                <CardContent className="p-12 text-center">
                  <div className="max-w-md mx-auto">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-brand-accent mb-4">
                      <ShoppingBag className="h-10 w-10 text-brand-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-brand-secondary mb-2">No orders yet</h3>
                    <p className="text-gray-600 mb-6">Start exploring beautiful flower arrangements from local florists</p>
                    <Button 
                      onClick={() => navigate('/search')}
                      className="bg-[#967F81] hover:bg-[#e63e1c] text-white shadow-md px-8 py-6 text-base"
                    >
                      Browse Flowers
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {orders?.map((order) => (
                  <Card key={order.id} className="rounded-xl bg-white shadow-lg border border-brand-primary/10 hover:shadow-xl transition-all overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-brand-secondary">Order {order.order_number || `#${order.id.slice(0, 8)}`}</h3>
                          <p className="text-sm text-gray-600 mt-1 flex items-center gap-2">
                            <User className="h-4 w-4 text-brand-primary" />
                            {order.store_profiles?.store_name || "Florist"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-brand-primary">${order.total_amount}</p>
                          <p className="text-sm text-gray-500 mt-1">
                            {new Date(order.created_at).toLocaleDateString('en-AU', { 
                              day: 'numeric', 
                              month: 'short', 
                              year: 'numeric' 
                            })}
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                        <div className="text-sm">
                          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold capitalize shadow-sm
                            ${order.status === 'completed' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' : 
                              order.status === 'cancelled' ? 'bg-red-100 text-red-700 border border-red-200' : 
                              'bg-blue-100 text-blue-700 border border-blue-200'}`}
                          >
                            {order.status}
                          </span>
                        </div>
                        <Button 
                          variant="outline" 
                          onClick={() => navigate(`/order/${order.id}`)}
                          className="border-brand-primary text-brand-primary bg-[#967F81] hover:bg-[#843329]/10"
                        >
                          View Details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CustomerDashboard;